import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import { useTranslation } from "react-i18next";
import {
  FAQ_RETRIEVAL_PROFILES,
  buildRetrievalFromProfile,
  getEffectiveFaqRetrieval,
  getEffectiveFaqRouting,
} from "../../utils/faqSkillConfig";
import { suggestAgentKnowledgeSearchHints } from "../../api/client";

const PROFILE_ORDER = ["precise", "balanced", "broad"];

function groupsToText(groups) {
  return (Array.isArray(groups) ? groups : [])
    .map((g) => (Array.isArray(g) ? g.join(", ") : String(g || "")))
    .filter(Boolean);
}

function parseTermLine(line) {
  return String(line || "")
    .split(/[,;]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);
}

/** Ícone de IA padrão da plataforma, com tooltip explicando o que a análise faz. */
function SuggestButton({ label, disabledLabel, enabled, loading, onClick }) {
  return (
    <Tooltip title={enabled ? label : disabledLabel}>
      <span>
        <IconButton
          size="small"
          color="primary"
          onClick={onClick}
          disabled={!enabled || loading}
          aria-label={label}
        >
          {loading ? <CircularProgress size={18} /> : <AutoAwesomeIcon fontSize="small" />}
        </IconButton>
      </span>
    </Tooltip>
  );
}

/**
 * Configuração de busca do FAQ: perfil de rigor, termos equivalentes e
 * assuntos que devem ir direto para atendimento humano.
 */
export default function FaqRetrievalConfigSection({
  skillConfig,
  disabled,
  onChange,
  documents = [],
  sourceUrls = [],
}) {
  const { t } = useTranslation("settings");
  const retrieval = useMemo(() => getEffectiveFaqRetrieval(skillConfig), [skillConfig]);
  const routing = useMemo(() => getEffectiveFaqRouting(skillConfig), [skillConfig]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState("");
  const [suggestTarget, setSuggestTarget] = useState("aliases");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [hints, setHints] = useState(null);

  const actionRules = useMemo(() => routing.action_rules || [], [routing.action_rules]);
  const hasKnowledge = (documents?.length || 0) > 0 || (sourceUrls?.length || 0) > 0;

  const patchRetrieval = useCallback(
    (partial) => {
      onChange?.({
        retrieval: {
          ...(skillConfig?.retrieval || { profile: retrieval.profile }),
          ...partial,
        },
      });
    },
    [onChange, skillConfig?.retrieval, retrieval.profile]
  );

  const patchRouting = useCallback(
    (partial) => {
      onChange?.({ routing: { ...(skillConfig?.routing || {}), ...partial } });
    },
    [onChange, skillConfig?.routing]
  );

  const applyProfile = (profile) => {
    if (disabled || !FAQ_RETRIEVAL_PROFILES[profile]) return;
    onChange?.({
      retrieval: { ...(skillConfig?.retrieval || {}), ...buildRetrievalFromProfile(profile) },
    });
  };

  // Edição em texto livre por linha; só grupos com 2+ termos vão para a config.
  // Manter o rascunho local evita perder o que está sendo digitado.
  const [aliasLines, setAliasLines] = useState(() => groupsToText(retrieval.alias_groups));
  const lastSyncedSignature = useRef(JSON.stringify(retrieval.alias_groups || []));

  useEffect(() => {
    const signature = JSON.stringify(retrieval.alias_groups || []);
    if (signature === lastSyncedSignature.current) return;
    lastSyncedSignature.current = signature;
    setAliasLines(groupsToText(retrieval.alias_groups));
  }, [retrieval.alias_groups]);

  const commitAliasLines = (lines) => {
    setAliasLines(lines);
    const groups = lines.map(parseTermLine).filter((g) => g.length >= 2);
    lastSyncedSignature.current = JSON.stringify(groups);
    patchRetrieval({ alias_groups: groups });
  };

  const [ruleDrafts, setRuleDrafts] = useState(() => actionRules);
  const lastSyncedRules = useRef(JSON.stringify(actionRules));

  useEffect(() => {
    const signature = JSON.stringify(actionRules);
    if (signature === lastSyncedRules.current) return;
    lastSyncedRules.current = signature;
    setRuleDrafts(actionRules);
  }, [actionRules]);

  const setAliasLine = (index, value) => {
    const next = aliasLines.slice();
    next[index] = value;
    commitAliasLines(next);
  };

  const addAliasLine = () => setAliasLines((lines) => [...lines, ""]);

  const removeAliasLine = (index) => commitAliasLines(aliasLines.filter((_, i) => i !== index));

  // Uma análise da base alimenta as duas seções, então o resultado fica em cache.
  const analyzeKnowledge = async () => {
    const res = await suggestAgentKnowledgeSearchHints({
      documents: (documents || []).map((d) => ({
        file_url: d?.file_url || d?.fileUrl || d?.url,
        file_name: d?.file_name || d?.fileName || d?.name,
        content_type: d?.content_type || d?.contentType,
      })),
      source_urls: sourceUrls || [],
      existing_groups: retrieval.alias_groups || [],
    });
    const hints = {
      groups: Array.isArray(res?.groups) ? res.groups : [],
      handoffTopics: Array.isArray(res?.handoffTopics) ? res.handoffTopics : [],
    };
    setHints(hints);
    return hints;
  };

  const openSuggestions = async (target) => {
    if (disabled || !hasKnowledge) return;
    setSuggestTarget(target);
    setSuggestOpen(true);
    setSuggestError("");
    setSelectedSuggestions({});

    let available = hints;
    if (!available) {
      setSuggestions([]);
      setSuggestLoading(true);
      try {
        available = await analyzeKnowledge();
      } catch (err) {
        setSuggestError(err?.message || t("agentsListPage.faqAliasSuggestError"));
        setSuggestLoading(false);
        return;
      }
      setSuggestLoading(false);
    }

    const list = target === "handoff" ? available.handoffTopics : available.groups;
    setSuggestions(list);
    setSelectedSuggestions(Object.fromEntries(list.map((_, i) => [i, true])));
    if (!list.length) setSuggestError(t("agentsListPage.faqAliasSuggestEmpty"));
  };

  const applySuggestions = () => {
    const chosen = suggestions.filter((_, i) => selectedSuggestions[i]);
    if (chosen.length && suggestTarget === "handoff") {
      const seen = new Set(ruleDrafts.map((r) => (r.keywords || []).join("|")));
      const merged = ruleDrafts.slice();
      for (const topic of chosen) {
        const sig = (topic.keywords || []).join("|");
        if (!sig || seen.has(sig)) continue;
        seen.add(sig);
        merged.push(topic);
      }
      commitActionRules(merged);
    } else if (chosen.length) {
      const existing = aliasLines.map(parseTermLine).filter((g) => g.length >= 2);
      const seen = new Set(existing.map((g) => [...g].sort().join("|")));
      const merged = existing.slice();
      for (const group of chosen) {
        const sig = [...group].sort().join("|");
        if (seen.has(sig)) continue;
        seen.add(sig);
        merged.push(group);
      }
      commitAliasLines(groupsToText(merged));
    }
    setSuggestOpen(false);
  };

  // Mesma ideia dos termos: um assunto sem palavras não decide nada, então só
  // vai para a config quando estiver preenchido.
  const commitActionRules = (rules) => {
    setRuleDrafts(rules);
    const ready = rules.filter((r) => (r.keywords || []).length > 0);
    lastSyncedRules.current = JSON.stringify(ready);
    patchRouting({ action_rules: ready });
  };

  const setActionRule = (index, partial) =>
    commitActionRules(ruleDrafts.map((r, i) => (i === index ? { ...r, ...partial } : r)));

  const addActionRule = () =>
    setRuleDrafts((rules) => [...rules, { intent: "", label: "", keywords: [], dest: "fila_humano" }]);

  const removeActionRule = (index) =>
    commitActionRules(ruleDrafts.filter((_, i) => i !== index));

  return (
    <Stack spacing={2.5}>
      <Alert severity="info" variant="outlined">
        {t("agentsListPage.faqRetrievalIntro")}
      </Alert>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {t("agentsListPage.faqRetrievalProfileTitle")}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {t("agentsListPage.faqRetrievalProfileHelper")}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          {PROFILE_ORDER.map((key) => {
            const preset = FAQ_RETRIEVAL_PROFILES[key];
            const selected = retrieval.profile === key;
            return (
              <Paper
                key={key}
                variant="outlined"
                onClick={() => applyProfile(key)}
                sx={{
                  flex: 1,
                  p: 1.5,
                  cursor: disabled ? "default" : "pointer",
                  borderWidth: selected ? 2 : 1,
                  borderColor: selected ? "primary.main" : "divider",
                  bgcolor: selected ? "action.selected" : "background.paper",
                  opacity: disabled ? 0.7 : 1,
                  transition: "border-color .15s, background-color .15s",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {t(preset.labelKey)}
                  </Typography>
                  {key === "balanced" && (
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={t("agentsListPage.faqRetrievalRecommended")}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {t(preset.hintKey)}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
            {t("agentsListPage.faqAliasTitle")}
          </Typography>
          <SuggestButton
            label={t("agentsListPage.faqAliasSuggestTooltip")}
            disabledLabel={t("agentsListPage.faqAliasSuggestNeedKnowledge")}
            enabled={!disabled && hasKnowledge}
            loading={suggestLoading && suggestTarget === "aliases"}
            onClick={() => openSuggestions("aliases")}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {t("agentsListPage.faqAliasHelper")}
        </Typography>

        <Stack spacing={1}>
          {aliasLines.map((line, index) => (
            <Stack key={`alias-${index}`} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                value={line}
                disabled={disabled}
                onChange={(e) => setAliasLine(index, e.target.value)}
                placeholder={t("agentsListPage.faqAliasPlaceholder")}
                helperText={
                  parseTermLine(line).length < 2 ? t("agentsListPage.faqAliasMinTerms") : undefined
                }
              />
              <IconButton
                size="small"
                onClick={() => removeAliasLine(index)}
                disabled={disabled}
                aria-label={t("agentsListPage.faqAliasRemove")}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addAliasLine}
            disabled={disabled}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            {t("agentsListPage.faqAliasAdd")}
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
            {t("agentsListPage.faqHandoffTopicsTitle")}
          </Typography>
          <SuggestButton
            label={t("agentsListPage.faqHandoffSuggestTooltip")}
            disabledLabel={t("agentsListPage.faqAliasSuggestNeedKnowledge")}
            enabled={!disabled && hasKnowledge}
            loading={suggestLoading && suggestTarget === "handoff"}
            onClick={() => openSuggestions("handoff")}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {t("agentsListPage.faqHandoffTopicsHelper")}
        </Typography>
        <Stack spacing={1}>
          {ruleDrafts.map((rule, index) => (
            <Stack key={`rule-${index}`} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                label={t("agentsListPage.faqHandoffTopicName")}
                value={rule.label || rule.intent || ""}
                disabled={disabled}
                onChange={(e) => setActionRule(index, { label: e.target.value, intent: e.target.value })}
                sx={{ minWidth: 160 }}
              />
              <TextField
                size="small"
                fullWidth
                label={t("agentsListPage.faqHandoffTopicKeywords")}
                value={Array.isArray(rule.keywords) ? rule.keywords.join(", ") : ""}
                disabled={disabled}
                onChange={(e) => setActionRule(index, { keywords: parseTermLine(e.target.value) })}
                placeholder={t("agentsListPage.faqHandoffTopicKeywordsPlaceholder")}
              />
              <IconButton
                size="small"
                onClick={() => removeActionRule(index)}
                disabled={disabled}
                aria-label={t("agentsListPage.faqHandoffTopicRemove")}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addActionRule}
            disabled={disabled}
            sx={{ alignSelf: "flex-start", textTransform: "none" }}
          >
            {t("agentsListPage.faqHandoffTopicAdd")}
          </Button>
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Button
          size="small"
          startIcon={<TuneIcon />}
          onClick={() => setShowAdvanced((v) => !v)}
          sx={{ textTransform: "none" }}
        >
          {showAdvanced
            ? t("agentsListPage.faqRetrievalHideAdvanced")
            : t("agentsListPage.faqRetrievalShowAdvanced")}
        </Button>

        {showAdvanced && (
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("agentsListPage.faqRetrievalTopK")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("agentsListPage.faqRetrievalTopKHelper")}
              </Typography>
              <Slider
                size="small"
                value={retrieval.top_k}
                min={3}
                max={20}
                step={1}
                marks={[
                  { value: 3, label: "3" },
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                ]}
                valueLabelDisplay="auto"
                disabled={disabled}
                onChange={(_e, value) => patchRetrieval({ top_k: Number(value) })}
                sx={{ maxWidth: 420 }}
              />
            </Box>

            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {t("agentsListPage.faqRetrievalMinScore")}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {t("agentsListPage.faqRetrievalMinScoreHelper")}
              </Typography>
              <Slider
                size="small"
                value={retrieval.min_lexical_score}
                min={1}
                max={6}
                step={0.5}
                marks={[
                  { value: 1, label: t("agentsListPage.faqRetrievalMinScoreLow") },
                  { value: 2, label: t("agentsListPage.faqRetrievalMinScoreMid") },
                  { value: 6, label: t("agentsListPage.faqRetrievalMinScoreHigh") },
                ]}
                valueLabelDisplay="auto"
                disabled={disabled}
                onChange={(_e, value) => patchRetrieval({ min_lexical_score: Number(value) })}
                sx={{ maxWidth: 420 }}
              />
            </Box>

            <TextField
              label={t("agentsListPage.faqRoutingFallback")}
              helperText={t("agentsListPage.faqRoutingFallbackHelper")}
              value={routing.redirect_fallback_reply}
              disabled={disabled}
              onChange={(e) => patchRouting({ redirect_fallback_reply: e.target.value })}
              fullWidth
              size="small"
              multiline
              minRows={2}
            />
          </Stack>
        )}
      </Box>

      <Dialog
        open={suggestOpen}
        onClose={() => !suggestLoading && setSuggestOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon color="primary" fontSize="small" />
          {suggestTarget === "handoff"
            ? t("agentsListPage.faqHandoffSuggestDialogTitle")
            : t("agentsListPage.faqAliasSuggestDialogTitle")}
        </DialogTitle>
        <DialogContent dividers>
          {suggestLoading && (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
              <CircularProgress size={28} />
              <Typography variant="body2" color="text.secondary">
                {t("agentsListPage.faqAliasSuggestLoading")}
              </Typography>
            </Stack>
          )}

          {!suggestLoading && suggestError && (
            <Alert severity="info" variant="outlined">
              {suggestError}
            </Alert>
          )}

          {!suggestLoading && !suggestError && suggestions.length > 0 && (
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {suggestTarget === "handoff"
                  ? t("agentsListPage.faqHandoffSuggestDialogHint")
                  : t("agentsListPage.faqAliasSuggestDialogHint")}
              </Typography>
              {suggestions.map((suggestion, index) => {
                const terms = Array.isArray(suggestion) ? suggestion : suggestion?.keywords || [];
                const heading = Array.isArray(suggestion) ? null : suggestion?.label;
                return (
                  <FormControlLabel
                    key={`suggestion-${index}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={Boolean(selectedSuggestions[index])}
                        onChange={(e) =>
                          setSelectedSuggestions((prev) => ({ ...prev, [index]: e.target.checked }))
                        }
                      />
                    }
                    label={
                      <Box>
                        {heading && (
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {heading}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                          {terms.map((term) => (
                            <Chip key={term} size="small" label={term} variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    }
                    sx={{ alignItems: "center", m: 0, py: 0.5 }}
                  />
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestOpen(false)} disabled={suggestLoading} sx={{ textTransform: "none" }}>
            {t("agentsListPage.faqAliasSuggestCancel")}
          </Button>
          <Button
            variant="contained"
            onClick={applySuggestions}
            disabled={suggestLoading || !suggestions.some((_, i) => selectedSuggestions[i])}
            sx={{ textTransform: "none" }}
          >
            {t("agentsListPage.faqAliasSuggestApply")}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
